import React, { useState, useEffect } from "react";
import { apiClient } from "../utils/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Settings,
  Mail,
  MessageSquare,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Sun,
  Send,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

// ─── Timezone options ──────────────────────────────────────────────────────────
const TIMEZONES = [
  { label: "UTC", value: "UTC" },
  { label: "IST — Asia/Kolkata (UTC+5:30)", value: "Asia/Kolkata" },
  { label: "GST — Asia/Dubai (UTC+4)", value: "Asia/Dubai" },
  { label: "EST — America/New_York (UTC-5)", value: "America/New_York" },
  { label: "CST — America/Chicago (UTC-6)", value: "America/Chicago" },
  { label: "MST — America/Denver (UTC-7)", value: "America/Denver" },
  { label: "PST — America/LA (UTC-8)", value: "America/Los_Angeles" },
  { label: "GMT — Europe/London", value: "Europe/London" },
  { label: "CET — Europe/Paris (UTC+1)", value: "Europe/Paris" },
  { label: "SGT — Asia/Singapore (UTC+8)", value: "Asia/Singapore" },
  { label: "JST — Asia/Tokyo (UTC+9)", value: "Asia/Tokyo" },
  { label: "AEST — Australia/Sydney (UTC+10)", value: "Australia/Sydney" },
];

// ─── Tiny helpers ──────────────────────────────────────────────────────────────
const StatusIcon = ({ ok }) =>
  ok ? (
    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
  ) : (
    <XCircle className="w-5 h-5 text-rose-500   shrink-0" />
  );

const StatusBadge = ({ ok, labels = ["Active", "Not Configured"] }) => (
  <Badge
    className={
      ok
        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
        : "bg-rose-100 text-rose-800 border-rose-200"
    }
  >
    {ok ? labels[0] : labels[1]}
  </Badge>
);

// pad "6" → "06:00"
const toTimeString = (h, m) =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

// ─── Main component ────────────────────────────────────────────────────────────
export default function SystemSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // test inputs
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState({
    email: false,
    sms: false,
    reminder: false,
    digest: false,
    saveSchedule: false,
  });

  // digest schedule state
  const [schedule, setSchedule] = useState({
    time: "06:00", // "HH:MM" string — easier for <input type="time">
    timezone: "UTC",
    enabled: true,
  });
  const [scheduleLoaded, setScheduleLoaded] = useState(false);

  // ── Fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      await Promise.all([fetchStatus(), fetchSchedule()]);
      setLoading(false);
    })();
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await apiClient.get("/system/notification-status");
      setStatus(data);
    } catch {
      toast.error("Failed to load system status");
    }
  };

  const fetchSchedule = async () => {
    try {
      const { data } = await apiClient.get("/system/digest-schedule");
      setSchedule({
        time: toTimeString(data.hour, data.minute),
        timezone: data.timezone || "UTC",
        enabled: data.enabled ?? true,
      });
      setScheduleLoaded(true);
    } catch {
      toast.error("Failed to load digest schedule");
    }
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const mark = (key, val) => setBusy((b) => ({ ...b, [key]: val }));

  const testEmailSystem = async () => {
    if (!testEmail) return toast.error("Enter an email address first");
    mark("email", true);
    try {
      const { data } = await apiClient.post(
        `/test/send-email?email=${encodeURIComponent(testEmail)}`,
      );
      data.result?.success
        ? toast.success("Test email sent!")
        : toast.warning(`Email issues: ${data.result?.error || "Check logs"}`);
    } catch {
      toast.error("Failed to send test email");
    } finally {
      mark("email", false);
    }
  };

  const testSMSSystem = async () => {
    if (!testPhone) return toast.error("Enter a phone number first");
    mark("sms", true);
    try {
      const { data } = await apiClient.post(
        `/test/send-sms?phone=${encodeURIComponent(testPhone)}`,
      );
      data.result?.success
        ? toast.success("Test SMS sent!")
        : toast.warning(
            `SMS issues: ${data.result?.error || "Check configuration"}`,
          );
    } catch {
      toast.error("Failed to send test SMS");
    } finally {
      mark("sms", false);
    }
  };

  const triggerReminderCheck = async () => {
    mark("reminder", true);
    try {
      await apiClient.post("/reminders/check-now");
      toast.success("Reminder check done — see server logs for details");
    } catch {
      toast.error("Failed to trigger reminder check");
    } finally {
      mark("reminder", false);
    }
  };

  const triggerDigestNow = async () => {
    mark("digest", true);
    try {
      await apiClient.post("/digest/send-now");
      toast.success("Morning digest sent to all owners!");
    } catch {
      toast.error("Failed to send digest");
    } finally {
      mark("digest", false);
    }
  };

  const saveSchedule = async () => {
    mark("saveSchedule", true);
    try {
      const [hourStr, minuteStr] = schedule.time.split(":");
      const { data } = await apiClient.post("/system/digest-schedule", {
        hour: parseInt(hourStr, 10),
        minute: parseInt(minuteStr, 10),
        timezone: schedule.timezone,
        enabled: schedule.enabled,
      });
      toast.success(
        schedule.enabled
          ? `Digest scheduled for ${data.next_run} ✓`
          : "Morning digest disabled",
      );
    } catch {
      toast.error("Failed to save schedule");
    } finally {
      mark("saveSchedule", false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="system-settings-page">
      {/* ── Page header ── */}
      <div>
        <h1
          className="text-3xl lg:text-4xl font-bold mb-1"
          style={{ letterSpacing: "-0.02em" }}
        >
          System Settings
        </h1>
        <p className="text-muted-foreground text-sm lg:text-base">
          Monitor, test, and configure notification systems
        </p>
      </div>

      {/* ── Status overview cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: "Email System",
            icon: Mail,
            ok: status?.email?.configured,
            msg: status?.email?.message,
          },
          {
            label: "SMS System",
            icon: MessageSquare,
            ok: status?.sms?.configured,
            msg: status?.sms?.message,
          },
          {
            label: "Google Calendar",
            icon: Calendar,
            ok: status?.calendar?.configured,
            msg: status?.calendar?.message,
          },
          {
            label: "Reminders",
            icon: Clock,
            ok: status?.reminders?.scheduler_running,
            msg: status?.reminders?.message,
          },
        ].map(({ label, icon: Icon, ok, msg }) => (
          <Card key={label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{label}</CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-2">
                <StatusIcon ok={ok} />
                <span className="text-xs text-muted-foreground leading-snug">
                  {msg}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Email + SMS ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Email Configuration</CardTitle>
              <StatusBadge ok={status?.email?.configured} />
            </div>
            <CardDescription>Test email notification delivery</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status?.email?.warning && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  {status.email.warning}
                </p>
              </div>
            )}
            <div className="text-sm space-y-1">
              <p>
                <span className="font-medium">Sender:</span>{" "}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  {status?.email?.sender}
                </code>
              </p>
              <p>
                <span className="font-medium">Mode:</span>{" "}
                {status?.email?.test_mode ? (
                  <Badge variant="outline" className="text-xs">
                    Test
                  </Badge>
                ) : (
                  <Badge className="text-xs bg-emerald-100 text-emerald-800">
                    Production
                  </Badge>
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-email">Send a test email to</Label>
              <div className="flex gap-2">
                <Input
                  id="test-email"
                  type="email"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && testEmailSystem()}
                />
                <Button
                  onClick={testEmailSystem}
                  disabled={busy.email}
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                >
                  {busy.email ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMS */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>SMS Configuration</CardTitle>
              <StatusBadge ok={status?.sms?.configured} />
            </div>
            <CardDescription>
              Test SMS notification delivery via Twilio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm space-y-1">
              <p>
                <span className="font-medium">From:</span>{" "}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                  {status?.sms?.from_number || "Not set"}
                </code>
              </p>
            </div>
            {!status?.sms?.configured && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  Add <code className="font-mono">TWILIO_ACCOUNT_SID</code>,{" "}
                  <code className="font-mono">TWILIO_AUTH_TOKEN</code>, and{" "}
                  <code className="font-mono">TWILIO_PHONE_NUMBER</code> to your{" "}
                  <code className="font-mono">.env</code> to enable SMS.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="test-phone">Send a test SMS to</Label>
              <div className="flex gap-2">
                <Input
                  id="test-phone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && testSMSSystem()}
                  disabled={!status?.sms?.configured}
                />
                <Button
                  onClick={testSMSSystem}
                  disabled={busy.sms || !status?.sms?.configured}
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                >
                  {busy.sms ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Morning Digest Schedule ── */}
      <Card className="border-2 border-violet-100 dark:border-violet-900">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center shrink-0">
              <Sun className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle>Morning Digest Schedule</CardTitle>
              <CardDescription>
                Daily summary of meetings, shoots, editing tasks, ad campaigns,
                and new leads — sent to all Owners
              </CardDescription>
            </div>
            {/* Enable / disable toggle */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {schedule.enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={schedule.enabled}
                onCheckedChange={(val) =>
                  setSchedule((s) => ({ ...s, enabled: val }))
                }
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* ── Time + Timezone row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Time picker */}
            <div className="space-y-2">
              <Label
                htmlFor="digest-time"
                className="flex items-center gap-1.5"
              >
                <Clock className="w-3.5 h-3.5" />
                Send at (local time in selected zone)
              </Label>
              <Input
                id="digest-time"
                type="time"
                value={schedule.time}
                onChange={(e) =>
                  setSchedule((s) => ({ ...s, time: e.target.value }))
                }
                disabled={!schedule.enabled}
                className="font-mono text-lg h-11 w-full"
              />
            </div>

            {/* Timezone picker */}
            <div className="space-y-2">
              <Label htmlFor="digest-tz" className="flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5" />
                Timezone
              </Label>
              <select
                id="digest-tz"
                value={schedule.timezone}
                onChange={(e) =>
                  setSchedule((s) => ({ ...s, timezone: e.target.value }))
                }
                disabled={!schedule.enabled}
                className={[
                  "flex h-11 w-full rounded-md border border-input bg-background",
                  "px-3 py-2 text-sm ring-offset-background",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                ].join(" ")}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Preview banner ── */}
          {schedule.enabled && scheduleLoaded && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-violet-50 dark:bg-violet-950 border border-violet-200 dark:border-violet-800">
              <Sun className="w-4 h-4 text-violet-600 shrink-0" />
              <p className="text-sm text-violet-800 dark:text-violet-200">
                Digest will fire every day at{" "}
                <strong className="font-mono">{schedule.time}</strong>{" "}
                <span className="text-violet-600 dark:text-violet-400">
                  (
                  {TIMEZONES.find((t) => t.value === schedule.timezone)
                    ?.label || schedule.timezone}
                  )
                </span>
              </p>
            </div>
          )}
          {!schedule.enabled && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted border">
              <XCircle className="w-4 h-4 text-muted-foreground shrink-0" />
              <p className="text-sm text-muted-foreground">
                Morning digest is currently disabled
              </p>
            </div>
          )}

          {/* ── What's included ── */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Digest includes
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-muted-foreground">
              {[
                "📅 Today's scheduled meetings",
                "📸 Today's planned shoots",
                "⚠️ Overdue / due-today editing tasks",
                "📢 Currently live ad campaigns",
                "🔔 New uncontacted leads count",
              ].map((item) => (
                <li key={item} className="flex items-start gap-1.5">
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground pt-1">
              Sent as a formatted <strong>email</strong> + short{" "}
              <strong>SMS</strong> (if phone is set) to every Owner account.
            </p>
          </div>

          {/* ── Action buttons ── */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              onClick={saveSchedule}
              disabled={busy.saveSchedule}
              className="sm:w-auto"
            >
              {busy.saveSchedule ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save Schedule"
              )}
            </Button>
            <Button
              onClick={triggerDigestNow}
              disabled={busy.digest}
              variant="outline"
              className="sm:w-auto"
            >
              {busy.digest ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Digest Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Hourly Reminder System ── */}
      <Card>
        <CardHeader>
          <CardTitle>24-Hour Reminder System</CardTitle>
          <CardDescription>
            Automatic reminders sent 24 hours before meetings and shoots
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-1">Check Interval</p>
              <p className="text-sm text-muted-foreground">
                {status?.reminders?.check_interval}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Scheduler Status</p>
              <StatusBadge
                ok={status?.reminders?.scheduler_running}
                labels={["Running", "Stopped"]}
              />
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <p className="text-sm mb-1">Recipients:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
              <li>
                Scheduled meetings → owners + assigned staff (email + SMS)
              </li>
              <li>Planned shoots → owners + assigned team (email + SMS)</li>
            </ul>
          </div>
          <Button
            onClick={triggerReminderCheck}
            disabled={busy.reminder}
            variant="outline"
            className="w-full sm:w-auto"
          >
            {busy.reminder ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Checking…
              </>
            ) : (
              "Trigger Manual Check"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
